import { faColumns3, faRotateLeft } from '@fortawesome/pro-regular-svg-icons'
import { Modify } from '@teinor/erp'
import { useCallback, useState } from 'react'
import useTwinTranslation from '../../../../utils/hooks/useTwinTranslation'
import { ComponentTabObject } from '../../../../utils/hooks/useTabLogic'
import ModalTaskConfigColumns from './ModalTaskConfigColumns'
import ModalTaskConfigColumnsFilters from './ModalTaskConfigColumnsFilters'
import ModalTaskConfigColumnsOrder from './ModalTaskConfigColumnsOrder'
import ModalTabs from '../../../../baseComponents/ModalsLayouts/ModalTabs'
import { ModalComponentPropsWithoutSubmit } from '../../../../baseComponents/ModalsLayouts/types'
import { ConfigColumnsColumn } from '../../../../baseComponents/ModalsLayouts/ModalConfigColumnsTabs/types'
import { twinFetchPostJSON } from '../../../../utils/globals/data'
import { changeEmployeeParams } from '../../../../utils/reducers/reduxDispatch'
import { ButtonsLoadFiltersStateFull } from '../../../../baseComponents/Button/ButtonLoadFilter'
import { ButtonSecondary } from '../../../../baseComponents/Button'
import TwinIcon, { TwinIconToolTip } from '../../../../baseComponents/TwinIcon'
import TwinTrans from '../../../../baseComponents/TwinTrans'
import { ModalOpenedTy } from '../../../../baseComponents/Modal/types'
import { TwinDictionary } from '../../../../utils/globals/dictionary'
import { DictTaskTableHeaderTy, ModalTaskConfigColumnsBaseProps } from './types'
import ModalTaskConfigColumnsHeader from './ModalTaskConfigColumnsHeader'
import { getTaskOnlyDefaultColsAndHeader, getTaskTableSelectedColumns, SelectedTaskColsFiltersHeader } from '../../../../utils/hooks/tasks/useTaskConfigColumns'
import { TableInstanceType } from '../../../../baseComponents/TwinTable/types'
import { addHeadersToTmpColumnsMTC } from './functions'
import { flushSync } from 'react-dom'
import { getSingleEmployeeParam } from '../../../../utils/reducers/getters'

interface ModalTaskConfigColumnsTabsProps extends ModalTaskConfigColumnsTabsLogicProps {
    haveFiltersColumn?: boolean
}

const ModalTaskConfigColumnsTabs: React.FC<ModalTaskConfigColumnsTabsProps> = ({ setOpened, haveFiltersColumn = true, ...rest }) => {
    const { t } = useTwinTranslation()
    const logic = useModalTaskConfigColumnsTabsLogic({ setOpened, ...rest })
    const tabs: ComponentTabObject = {
        'main': { component: ModalTaskConfigColumns, text: t('configColumns', 'Configurar columnas') },
    }
    if (haveFiltersColumn) {
        tabs['filters'] = { component: ModalTaskConfigColumnsFilters, text: t('configFilters', 'Configurar filtros') }
        tabs['order'] = { component: ModalTaskConfigColumnsOrder, text: t('configOrder', 'Configurar orden') }
    }
    tabs['header'] = { component: ModalTaskConfigColumnsHeader, text: t('configHeader', 'Configurar cabecera') }
    return (
        <ModalTabs tabs={tabs} setOpened={setOpened} onSubmit={() => setOpened(null)} extraComponentData={logic} className='modal_config_columns_tabs' userPermissions={{n: 4, permission: 'delete'}}></ModalTabs>
    )
}

interface ModalTaskConfigColumnsTabsLogicProps extends ModalComponentPropsWithoutSubmit {
    setSelectedColsFiltersHeader: React.Dispatch<React.SetStateAction<SelectedTaskColsFiltersHeader>>
    selectedColsFiltersHeader: SelectedTaskColsFiltersHeader
    columns: ConfigColumnsColumn<any>[]
    headers: DictTaskTableHeaderTy
    name: string
    tableInstance: TableInstanceType
}


const useModalTaskConfigColumnsTabsLogic = ({ selectedColsFiltersHeader, columns, setSelectedColsFiltersHeader, setOpened, name, headers, tableInstance }: ModalTaskConfigColumnsTabsLogicProps): ModalTaskConfigColumnsBaseProps => {
    const [tmpColumns, setTmpColumns] = useState<SelectedTaskColsFiltersHeader>(addHeadersToTmpColumnsMTC(selectedColsFiltersHeader, headers))

    const updateFilterOnTable = useCallback(async (columns: SelectedTaskColsFiltersHeader, id: number) => {
        delete columns.id
        delete columns.name
        const myValue = JSON.stringify(columns)
        await twinFetchPostJSON('/api/app/config/filter/updateFilter', { id, value: myValue })
    }, [])

    const updateEmployeeParams = useCallback(async (columns: SelectedTaskColsFiltersHeader) => {
        delete columns.id
        delete columns.name
        const myName = 'table_' + name
        const myValue = JSON.stringify(columns)
        await twinFetchPostJSON('/api/app/config/employee/updateEmployeeParameter', { parameterKey: myName, value: myValue })
        changeEmployeeParams({ [myName]: myValue })
    }, [name])

    const updateParamsDecider = useCallback(async (columns: SelectedTaskColsFiltersHeader) => {
        if (tmpColumns.id && String(tmpColumns.id) !== '0') {
            await updateFilterOnTable(columns, tmpColumns.id)
        } else {
            await updateEmployeeParams(columns)
        }
    }, [tmpColumns.id, updateFilterOnTable, updateEmployeeParams])

    const onSave = useCallback(() => {
        if (!Object.keys(tmpColumns.selectedColumns).length) {
            const defaultCols = getTaskOnlyDefaultColsAndHeader(columns, headers)
            const myColumns = { ...defaultCols, filters: tmpColumns.filters, id: tmpColumns.id, name: tmpColumns.name }
            setSelectedColsFiltersHeader(myColumns)
            updateParamsDecider(myColumns)
        } else {
            setSelectedColsFiltersHeader({ ...tmpColumns })
            updateParamsDecider({ ...tmpColumns })
        }
        tableInstance?.changeOffset(0)
        setOpened(null)
        return false
    }, [tmpColumns, setSelectedColsFiltersHeader, setOpened, columns, updateParamsDecider, headers, tableInstance])

    return { tmpColumns, setTmpColumns, onSave, columns, selectedColsFiltersHeader, name, headers}
}


type ButtonTaskConfigColumnsProps = Modify<ModalTaskConfigColumnsTabsProps, {
    setOpened?: never
    hideLoadFiltersButton?: boolean
}>

export const ButtonTaskConfigColumnsWithModal: React.FC<ButtonTaskConfigColumnsProps> = ({ selectedColsFiltersHeader, setSelectedColsFiltersHeader, hideLoadFiltersButton, tableInstance, ...rest }) => {
    const [opened, setOpened] = useState<ModalOpenedTy>(null)
    const { t } = useTwinTranslation()
    const filtersLength = Object.keys(selectedColsFiltersHeader?.filters || {}).length
    const orderLength = Object.keys(selectedColsFiltersHeader?.order || {}).length
    const isCustomFilter = selectedColsFiltersHeader.id && String(selectedColsFiltersHeader.id) !== '0'
    const name = isCustomFilter && selectedColsFiltersHeader.name ? selectedColsFiltersHeader.name : t('byDefault', 'Por defecto')
    const defaultValues = getTaskTableSelectedColumns(rest.name, rest.columns, rest.headers)
    const { onChangeFilter, resetFilter } = useButtonTaskConfigColumnsWithModalLogic({ tableInstance, setSelectedColsFiltersHeader, name: rest.name || '' , defaultValues})
    const showReset = !filtersLength && !isCustomFilter && (orderLength || (Object.keys(selectedColsFiltersHeader?.filterHeader || {})?.length && selectedColsFiltersHeader?.filterHeader['generalSearchBar'] !== ''))
    return (
        <div className={'button_config_columns_modal flex items-center flex-wrap gap-10'}>
            {filtersLength > 0 || isCustomFilter ?
                <div className='flex items-center'>
                    <span className='medium16'><TwinTrans transKey='configuration'>Configuración</TwinTrans>: {name} ({filtersLength})</span>
                    <TwinIconToolTip classNameIcon='flex items-end text-green-21 ml-10' message={t('deleteFilter', 'Borrar filtro')} id='resetFilter' icon={faRotateLeft} onClick={resetFilter} />
                </div>
            : null}
            {showReset ? <div className='flex items-center text-green-21 regular16 cursor-pointer reset_button_config_columns' onClick={resetFilter}>
                <span className='mr-10'>Reiniciar</span>
                <TwinIcon icon={faRotateLeft} />
            </div> : null}

            {hideLoadFiltersButton ? null : <ButtonsLoadFiltersStateFull allowCreate={false} selectedFilters={selectedColsFiltersHeader} onChangeFilter={(json) => onChangeFilter(json)} tableName={rest.name} defaultValues={defaultValues} className='load_button_filter' />}
            <ButtonSecondary onClick={() => setOpened(true)} className='h-34'>
                <TwinIcon icon={faColumns3} />
                <span className='ml-10 load_columns_button_text'><TwinTrans transKey='configColumns'>Configurar columnas</TwinTrans></span>
            </ButtonSecondary>
            {opened && <ModalTaskConfigColumnsTabs {...rest} setOpened={setOpened} selectedColsFiltersHeader={selectedColsFiltersHeader} setSelectedColsFiltersHeader={setSelectedColsFiltersHeader} tableInstance={tableInstance} />}
        </div>
    )
}

interface ButtonTaskConfigColumnsWithModalLogicProps {
    name: string
    setSelectedColsFiltersHeader: React.Dispatch<React.SetStateAction<SelectedTaskColsFiltersHeader>>
    tableInstance: TableInstanceType
    defaultValues: TwinDictionary
}

const useButtonTaskConfigColumnsWithModalLogic = ({ setSelectedColsFiltersHeader, tableInstance, name, defaultValues}: ButtonTaskConfigColumnsWithModalLogicProps) => {
    const onChangeFilter = useCallback((json: TwinDictionary) => {
        setSelectedColsFiltersHeader({ filters: json?.value?.filters || {}, selectedColumns: json?.value?.selectedColumns || {}, id: json.id, name: json.name, order: json?.value?.order || {}, selectedHeader: json?.value?.selectedHeader || {}, filterHeader: json?.value?.filterHeader || {} })
        tableInstance?.changeOffset(0)
    }, [setSelectedColsFiltersHeader, tableInstance])

    const myDefaultParsed = JSON.stringify(defaultValues)

    const resetFilter = useCallback(async () => {
        const myName = 'table_' + name
        await flushSync(async () => {
            let data: TwinDictionary = {}
            let isCustomFilter = false
            await setSelectedColsFiltersHeader((old) => {
                const copyOld = JSON.parse(JSON.stringify(old))
                if (copyOld.id && String(copyOld.id) !== '0') {
                    isCustomFilter = true
                    const table = getSingleEmployeeParam(myName) || '{}'
                    if (table) {
                        const myTableParsed = JSON.parse(table)
                        if (myTableParsed.selectedColumns) {
                            copyOld.selectedColumns = myTableParsed.selectedColumns
                        } else {
                            copyOld.selectedColumns = JSON.parse(myDefaultParsed).selectedColumns
                        }
                    }
                }
                data = { ...copyOld, filters: {}, order: {}, filterHeader: {}, name: '', id: '0' }
                return { ...copyOld, filters: {}, order: {}, filterHeader: {}, name: '', id: '0' }
            })
            tableInstance?.getTableDataFromStart(true)
            if (!isCustomFilter && getSingleEmployeeParam('saveTaskTableDefaultFilterWhenChanges') === 'on') {
                delete data.id
                delete data.name
                const dataStr = JSON.stringify(data)
                await twinFetchPostJSON('/api/app/config/employee/updateEmployeeParameter', { parameterKey: myName, value: dataStr })
                await changeEmployeeParams({ [myName]: dataStr })
            }

        })
    }, [name, setSelectedColsFiltersHeader, myDefaultParsed, tableInstance])

    return { onChangeFilter, resetFilter }
}

export default ModalTaskConfigColumnsTabs